/** @license
 *
 * Copyright 2024 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

import "isomorphic-fetch";

import { createJSONLoader, fetchMessageBundle, registerMessageBundleLoader } from "@arcgis/core/intl";
import { decideAndSetLocale, decideAndSetDir } from "./t9nUtils";

(function initError() {
  // Init i18n support
  configureIntlLoader();
  const locale = decideAndSetLocale(window?.location?.search, undefined, window?.navigator?.language);
  decideAndSetDir(locale);

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchMessageBundle("deployment/strings")
  .then(
    (translations) => {
      const errorHeadline = '<h1 class="error-headline error-headline-flex headline-size-large">' +
        '<img class="headline-logo" aria-hidden="true" src="./assets/images/solutions-icon.svg" width="60" height="60" alt="" aria-label="Solutions">' +
        'ArcGIS Solutions' +
        '</h1>';

        const browserLogoList = '<ul class="error-logos">' +
          '<li class="error-logo">' +
            '<a href="https://www.google.com/chrome/" class="error-logo-link" aria-label="to Chrome browser">' +
              '<img alt="Chrome" src="./assets/images/chrome_logo.png" aria-hidden="true" loading="lazy" class="error-logo-img">' +
            '</a>' +
          '</li>' +
          '<li class="error-logo">' +
            '<a href="https://www.mozilla.org/firefox/" class="error-logo-link" aria-label="to Firefox browser">' +
              '<img alt="Firefox" src="./assets/images/firefox_logo.png" aria-hidden="true" loading="lazy" class="error-logo-img">' +
            '</a>' +
          '</li>' +
          '<li class="error-logo">' +
            '<a href="https://www.apple.com/safari/" class="error-logo-link" aria-label="to Safari browser">' +
              '<img alt="Safari" src="./assets/images/safari_logo.png" aria-hidden="true" loading="lazy" class="error-logo-img">' +
            '</a>' +
          '</li>' +
          '<li class="error-logo">' +
            '<a href="https://www.microsoft.com/edge" class="error-logo-link" aria-label="to Edge browser">' +
              '<img alt="Edge" src="./assets/images/edge_logo.png" aria-hidden="true" loading="lazy" class="error-logo-img">' +
            '</a>' +
          '</li>' +
        '</ul>'

      // Generate the display and message
      const messageBegin = '<p tabindex="0" class="error-message message-size-large">'
      const messageEnd = '</p>'
      let errorMessage = "";
      errorMessage += messageBegin;

      const urlParams = new URLSearchParams(location.search);
      const errorType = urlParams.get("error");
      switch (errorType) {
        case "blocked":
          errorMessage += translations.elm.user.blockedAccess + "<br/>" + translations.elm.user.blockedSuggestion + messageEnd;
          break;
        case "browser":
          errorMessage += translations.elm.browser.notSupported + " " + translations.elm.browser.browserSuggestion + " "
          errorMessage += translations.feedback.message
          errorMessage += messageEnd;
          errorMessage += browserLogoList;
          break;
        case "browser_enterprise":
          errorMessage += translations.elm.browser.notSupported + " " + translations.elm.browser.browserSuggestion
          errorMessage += messageEnd;
          errorMessage += browserLogoList;
          break;
        default:
          errorMessage += translations.elm.browser.notSupported + " " + translations.elm.browser.browserSuggestion
          errorMessage += messageEnd;
          errorMessage += browserLogoList;
          break;
      }

      const errorDiv = document.querySelector('#error-msg');
      if (errorDiv) {
        errorDiv.innerHTML = errorHeadline + errorMessage;
      }
    }
  );
})();

/**
 * Configures the JSAPI locale loader.
 */
function configureIntlLoader(
): void {
  registerMessageBundleLoader(
    createJSONLoader({
      pattern: "deployment/",
      base: "",
      location: new URL("./assets/", window.location.href)
    })
  );
}
